// @ts-check
import { Component } from "../engine";
import { Vector2D } from "../engine/vector";
import { Graphics } from "../engine/graphics";
import { DEG_TO_RAD, ENTITY_STATE_DEFAULT, ENTITY_STATE_EXPLODING, ENTITY_STATE_CHARRED, BALL_TYPE_BOMB, LOGIC_GAME_INTRO, LOGIC_GAME_FINISHED } from "../global";

/**
 * Game Renderer
 */
export class GameRenderer extends Component {
    constructor() {
        super();
        this.temp = new Vector2D();
    }

    init() {
        var state = this.state;
        state.player1.load(state.player1Resource != undefined ? state.player1Resource : "player1");
        state.player1.position = state.player1Start.clone();
        state.player2.load(state.player2Resource != undefined ? state.player2Resource : "player2");
        state.player2.position = state.player2Start.clone();

        // Load level
        state.level.load(state.levelResource != undefined ? state.levelResource : "beach");
        if (state.level.gravity != undefined) {
            state.gravity = state.level.gravity;
        }

        if (state.level.playerJumpForce != undefined) {
            state.playerJumpForce = state.level.playerJumpForce;
        }

        if (state.level.ballMaxSpeed != undefined) {
            state.ball.maxSpeed = state.level.ballMaxSpeed;
        }

        // Load drawables
        this.drawableNet = new Graphics("net");
        this.drawableNet.recalcBounds = function(bounds) {
            bounds.x1 = -1;
            bounds.x2 = 1;
            return bounds;
        };

        this.drawableShadow = new Graphics("shadow");
        this.drawableBall = new Graphics("ball");
        this.drawableExplosion = new Graphics("explosion");
        this.drawableExplosionMark = new Graphics("explosion_mark");
        this.drawableBomb = new Graphics("bomb");
        this.drawableFuse = new Graphics("fuse");
        this.drawableFuse.recalcBounds = function(bounds) {
            bounds.x1 = 0;
            bounds.x2 = 1;
            return bounds;
        };

        this.drawableFuseGlow = new Graphics("fuse_glow");
        this.drawableSparkles1 = new Graphics("sparkles1");
        this.drawableSparkles2 = new Graphics("sparkles2");
        this.drawablePlayerSmoke = new Graphics("player_smoke");
        this.drawablePlayerSmoke.recalcBounds = function(bounds) {
            bounds.y1 = -bounds.height();
            bounds.y2 = 1;
            return bounds;
        };

        this.drawableEye = new Graphics("eye");
        this.drawableIndicator = new Graphics("indicator");
    }

    render() {
        // Init
        var context = this.context;

        // Set world transformation
        var w = 1024;
        var h = 600;
        var a = h / 2 / 100;
        var ax = (window.innerHeight / window.innerWidth) * (w / h);
        var t = [a * ax, 0, 0, a, w / 2, h / 2];
        this.state.projection = t;

        // Render
        context.save();
        context.setTransform(t[0], t[1], t[2], t[3], t[4], t[5]);

        // Draw scene
        var lastZ = -1000;

        // Draw layers
        for (var i = 0; i < this.state.level.layers.length; i++) {
            //
            var layer = this.state.level.layers[i];

            // Check if z is 0 and draw dynamic sprites
            if (lastZ < 0 && layer.z >= 0) {
                // Draw dynamic sprites
                this.renderSprites(this.state, context);
            }

            // Draw layer
            this.renderLayer(this.state, context, layer);

            // Update last z
            lastZ = layer.z;
        }

        // Restore
        context.restore();
    }

    renderLayer(state, context, layer) {
        context.save();
        context.translate(layer.position.x + state.camera.x * layer.paralax + state.camera.x * layer.fake3d, layer.position.y);
        layer.graphics.draw(context);
        context.restore();
    }

    renderSprites(state, context) {
        // Do not draw sprites on intro
        if (LOGIC_GAME_INTRO == state.logic || LOGIC_GAME_FINISHED == state.logic) {
            return;
        }

        // Render sprites
        context.save();
        context.translate(state.camera.x, 0);
        this.renderNet(state, context);
        this.renderPlayersAndBall(state, context);
        context.restore();
    }

    renderNet(state, context) {
        context.save();
        context.translate(0, -15);
        context.scale(state.camera.x * state.cameraFake3DScale, 1);
        this.drawableNet.draw(context);
        context.restore();
    }

    renderPlayersAndBall(state, context) {
        // Draw ball shadow
        if (state.ball.state == ENTITY_STATE_DEFAULT) {
            this.renderShadow(state, context, state.ball.position.x);
        }

        // Draw player1 shadow
        this.renderShadow(state, context, state.player1.position.x);

        // Draw player2 shadow
        this.renderShadow(state, context, state.player2.position.x);

        // Draw explosion mark
        if (state.explosionMark != 0) {
            this.renderShadow(state, context, state.explosionMark, this.drawableExplosionMark);
        }

        // Draw ball
        this.renderBall(state, context);

        // Draw player 1
        this.renderPlayer(context, state.player1, state.ball.position, 1);

        // Draw player 2
        this.renderPlayer(context, state.player2, state.ball.position, -1);

        // Draw ball indicator
        if (state.ball.position.y < -100 - state.ball.radius) {
            context.save();
            context.translate(state.ball.position.x, -100 + this.drawableIndicator.getBounds().height());
            this.drawableIndicator.draw(context);
            context.restore();
        }
    }

    renderPlayer(context, player, eyeTarget, flip) {
        // Render player
        context.save();
        context.translate(player.position.x, player.position.y);
        context.scale(flip, 1);

        if (player.state == ENTITY_STATE_CHARRED && player.drawableCharred != undefined) {
            context.save();
            context.translate(0, player.center.y);
            var s = 1 + Math.cos(this.state.f * 0.2) * 0.2;
            context.scale(s, s);
            this.drawablePlayerSmoke.draw(context);
            context.restore();

            player.drawableCharred.draw(context);
        } else {
            player.drawable.draw(context);
        }

        if (!player.eyeless) {
            // Render eye 1
            if (eyeTarget != undefined && player.state == ENTITY_STATE_DEFAULT) {
                this.temp
                    .set(player.position)
                    .add(player.eye1.position)
                    .invert()
                    .add(eyeTarget)
                    .normalize()
                    .scale(player.eye1.radius);
            } else {
                this.temp.zero();
            }

            context.save();
            context.translate(player.eye1.position.x + flip * this.temp.x, player.eye1.position.y + this.temp.y);
            context.scale(player.eye1.size, player.eye1.size);
            this.drawableEye.draw(context);
            context.restore();

            // Render eye 2
            if (eyeTarget != undefined && player.state == ENTITY_STATE_DEFAULT) {
                this.temp
                    .set(player.position)
                    .add(player.eye2.position)
                    .invert()
                    .add(eyeTarget)
                    .normalize()
                    .scale(player.eye2.radius);
            }

            context.save();
            context.translate(player.eye2.position.x + flip * this.temp.x, player.eye2.position.y + this.temp.y);
            context.scale(player.eye2.size, player.eye2.size);
            this.drawableEye.draw(context);
            context.restore();
        }

        context.restore();
    }

    renderBall(state, context) {
        // Render ball
        context.save();
        context.translate(state.ball.position.x, state.ball.position.y);

        if (state.ball.type == BALL_TYPE_BOMB) {
            var s = 0;
            if (state.ball.state == ENTITY_STATE_EXPLODING) {
                if (state.ball.animExplosion <= 9.0) {
                    var r = Math.random();
                    s = 1 + r * 0.5;
                    context.scale(s, s);
                    this.drawableExplosion.draw(context);
                }
            } else {
                if (state.ball.fuse < 0.2) {
                    s = 1 + Math.cos(state.f) * 0.1;
                    context.scale(s, s);
                }

                this.drawableBomb.draw(context);
                context.rotate(state.ball.ancle * DEG_TO_RAD);
                context.translate(state.ball.radius * 0.8, 0);

                context.save();
                context.scale(20 * state.ball.fuse, 1.0);
                this.drawableFuse.draw(context);
                context.restore();

                context.translate(20 * state.ball.fuse, 0);
                this.drawableFuseGlow.draw(context);

                var r = Math.random();
                context.rotate(r * 360 * DEG_TO_RAD);
                s = 1 + r * 0.4;
                context.scale(s, s);
                if (r > 0.5) {
                    this.drawableSparkles1.draw(context);
                } else {
                    this.drawableSparkles2.draw(context);
                }
            }
        } else {
            context.rotate(state.ball.ancle * DEG_TO_RAD);
            this.drawableBall.draw(context);
        }

        context.restore();
    }

    renderShadow(state, context, x, drawable) {
        //
        if (drawable == undefined) {
            drawable = this.drawableShadow;
        }

        // Render shadow
        context.save();
        context.translate(x, state.ground);
        drawable.draw(context);
        context.restore();
    }
}
